import { ErrorMessage, Field, type FieldProps, Form, Formik } from 'formik';
import { object, string } from 'yup';

import { Button } from '@src/design-system/button';
import {
  DialogBody,
  DialogFooter,
  DialogSubClose,
  DialogSubHeader,
} from '@src/design-system/dialog';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { TiptapEditor } from '@shared/components/reusables/tiptap-editor';

import { useRef } from 'react';
import meetingNoteApi from '@src/features/participants/services/meeting-note-api';
import { useDialogContext } from '@src/design-system/dialog/use-dialog-context';

const createNoteSchema = object().shape({
  note: string()
    .required('Note is required')
    .min(3, 'Note must be at least 3 characters'),
});

export function CreateNoteSubModal({
  meetingId,
  note,
}: {
  meetingId: number;
  note?: { id: number; note: string };
}) {
  const { formikRef } = useDialogContext();

  const { mutateAsync: createNote, isPending } = meetingNoteApi.useCreate();
  const { mutateAsync: updateNote, isPending: isUpdatingNote } =
    meetingNoteApi.useUpdate();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleSubmit = (values: typeof initialValues) => {
    if (note?.id) {
      updateNote({
        id: note.id,
        note: values.note,
      }).then(() => buttonRef.current?.click());
    } else {
      createNote({
        meeting: meetingId,
        note: values.note,
      }).then(() => buttonRef.current?.click());
    }
  };

  const initialValues = { note: note?.note || '' };

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={createNoteSchema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <Form>
        <DialogSubHeader>Write Note</DialogSubHeader>
        <DialogBody>
          <Field name='note'>
            {(fieldProps: FieldProps<string>) => (
              <div>
                <TiptapEditor
                  label='Add Note'
                  value={fieldProps.field.value}
                  onChange={(e) =>
                    fieldProps.form.setFieldValue(fieldProps.field.name, e)
                  }
                  enableMention
                />
                <ErrorMessage
                  {...fieldProps.field}
                  component={CustomErrorMessage}
                />
              </div>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <DialogSubClose>
            <Button
              className='w-3/6 hidden'
              variant='primaryOutline'
              ref={buttonRef}
            >
              Close
            </Button>
          </DialogSubClose>
          <Button
            isLoading={isPending || isUpdatingNote}
            type='submit'
            className='flex-1'
          >
            Confirm
          </Button>
        </DialogFooter>
      </Form>
    </Formik>
  );
}
